import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { CheckCircle2, X, ShoppingBag, ArrowRight } from "lucide-react";
import { formatPrice } from "../lib/format";

/**
 * Sliding mini-cart drawer that appears for 6s after `add()` is called.
 * Listens for the `cart:added` window event emitted by CartContext.
 * Provides instant, unambiguous visual feedback to fix add-to-cart conversion.
 */
export default function CartDrawerNotification() {
  const { t } = useTranslation();
  const { items, count, subtotal } = useCart();
  const [lastAdded, setLastAdded] = useState(null);

  // Listen to add-to-cart events from CartContext.add()
  useEffect(() => {
    const handler = (e) => setLastAdded(e.detail || {});
    window.addEventListener("cart:added", handler);
    return () => window.removeEventListener("cart:added", handler);
  }, []);

  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (!lastAdded) return;
    const id = setTimeout(() => setLastAdded(null), 6000);
    return () => clearTimeout(id);
  }, [lastAdded]);

  if (!lastAdded) return null;

  const item = items.find(
    (i) => i.product_id === lastAdded.product_id && (i.variant_id || null) === (lastAdded.variant_id || null)
  ) || lastAdded;

  return (
    <div
      className="fixed top-4 right-4 left-4 sm:left-auto sm:top-6 sm:right-6 z-[70] max-w-sm sm:w-96 animate-in slide-in-from-right duration-300"
      role="status"
      aria-live="polite"
      data-testid="cart-drawer-notification"
    >
      <div className="bg-white text-[#001F3F] shadow-2xl border border-[#001F3F]/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#001F3F] text-white">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em]">
            <CheckCircle2 size={16} className="text-[#A6E0B5]" />
            {t("cart_notif.added")}
          </div>
          <button
            onClick={() => setLastAdded(null)}
            className="text-white/60 hover:text-white"
            aria-label={t("common.close")}
            data-testid="cart-drawer-close"
          >
            <X size={16} />
          </button>
        </div>
        {/* Body */}
        <div className="flex items-stretch gap-4 p-5">
          <Link
            to={`/product/${item.product_id}`}
            onClick={() => setLastAdded(null)}
            className="flex-shrink-0 w-16 h-16 bg-[#0A2A4F]/5 overflow-hidden"
          >
            {item.image && (
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            )}
          </Link>
          <div className="min-w-0 flex-1 flex flex-col justify-between">
            <div className="text-sm font-semibold leading-tight line-clamp-2" data-testid="cart-drawer-name">
              {item.name}
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-[#525252]">
                {t("cart_notif.quantity", { count: item.quantity })}
              </span>
              <span className="text-sm font-bold">{formatPrice(item.price)}</span>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="border-t border-[#001F3F]/10 bg-[#F5F5F5] px-5 py-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#525252] uppercase tracking-widest font-bold">
              {t("cart_notif.cart_total", { count })}
            </span>
            <span className="text-sm font-bold text-[#001F3F]">{formatPrice(subtotal)}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link
              to="/cart"
              onClick={() => setLastAdded(null)}
              className="inline-flex items-center justify-center gap-2 border border-[#001F3F] text-[#001F3F] hover:bg-[#001F3F]/5 transition-all uppercase tracking-[0.2em] text-[10px] font-bold py-2.5"
              data-testid="cart-drawer-view-cart"
            >
              <ShoppingBag size={12} /> {t("cart_notif.view_cart")}
            </Link>
            <Link
              to="/checkout"
              onClick={() => setLastAdded(null)}
              className="inline-flex items-center justify-center gap-2 bg-[#001F3F] text-white hover:bg-[#FFD700] hover:text-[#001F3F] transition-all uppercase tracking-[0.2em] text-[10px] font-bold py-2.5"
              data-testid="cart-drawer-checkout"
            >
              {t("cart_notif.checkout")} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
